import { useState, useCallback } from 'react';
import { absencesAPI } from '../api/absences';

/**
 * Hook personalizado para gestión de Ausencias
 */
export const useAbsences = () => {
  const [absences, setAbsences] = useState([]);
  const [types, setTypes] = useState([]);
  const [balance, setBalance] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    total: 0
  });

  // ============================================================================
  // CRUD
  // ============================================================================

  /**
   * Cargar ausencias con filtros y paginación
   */
  const loadAbsences = useCallback(async (filters = {}, page = 1, limit = 10) => {
    setLoading(true);
    setError(null);
    try {
      const response = await absencesAPI.getAll({ ...filters, page, limit });
      if (response.success) {
        setAbsences(response.absences || []);
        setPagination({
          page: response.page || 1,
          limit: response.limit || 10,
          totalPages: response.totalPages || 1,
          total: response.total || 0
        });
        return { success: true, absences: response.absences || [], pagination: response };
      }
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar ausencias';
      setError(errorMsg);
      setAbsences([]); // Asegurar que siempre sea un array
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtener ausencia por ID
   */
  const getById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await absencesAPI.getById(id);
      if (response.success) {
        return { success: true, absence: response.absence };
      }
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al obtener ausencia';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Crear nueva ausencia
   */
  const createAbsence = useCallback(async (data, documentos = []) => {
    setLoading(true);
    setError(null);
    try {
      const response = await absencesAPI.create(data, documentos);
      if (response.success) {
        setAbsences(prev => [response.absence, ...prev]);
        return { success: true, absence: response.absence };
      }
      // Error de negocio (validación, permisos): lo retornamos al formulario,
      // NO al estado global para que no aparezca como banner de módulo.
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al crear ausencia';
      // Igual: error controlado → el formulario lo muestra inline, no el módulo.
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================================
  // Aprobación y Gestión
  // ============================================================================

  /**
   * Aprobar ausencia
   */
  const aprobar = useCallback(async (id, comentarios = '') => {
    setLoading(true);
    setError(null);
    try {
      const response = await absencesAPI.aprobar(id, comentarios);
      if (response.success) {
        // Actualizar en la lista local
        setAbsences(prev => prev.map(abs =>
          abs.id === id
            ? { ...abs, estado: response.estado, nivel_aprobacion_actual: abs.nivel_aprobacion_actual + 1 }
            : abs
        ));
        console.log('success', response.message || 'Ausencia aprobada exitosamente');
        return { success: true, estado: response.estado, siguienteNivel: response.siguiente_nivel };
      }
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al aprobar ausencia';
      setError(errorMsg);
      console.error('error', errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Rechazar ausencia
   */
  const rechazar = useCallback(async (id, motivo) => {
    setLoading(true);
    setError(null);
    try {
      const response = await absencesAPI.rechazar(id, motivo);
      if (response.success) {
        // Actualizar en la lista local
        setAbsences(prev => prev.map(abs =>
          abs.id === id ? { ...abs, estado: 'rechazado', motivo_rechazo: motivo } : abs
        ));
        console.log('success', response.message || 'Ausencia rechazada');
        return { success: true };
      }
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al rechazar ausencia';
      setError(errorMsg);
      console.error('error', errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cancelar ausencia
   */
  const cancelar = useCallback(async (id, motivo = '') => {
    setLoading(true);
    setError(null);
    try {
      const response = await absencesAPI.cancelar(id, motivo);
      if (response.success) {
        // Actualizar en la lista local
        setAbsences(prev => prev.map(abs =>
          abs.id === id ? { ...abs, estado: 'cancelado' } : abs
        ));
        console.log('success', response.message || 'Ausencia cancelada');
        return { success: true };
      }
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cancelar ausencia';
      setError(errorMsg);
      console.error('error', errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================================
  // Tipos de Ausencias
  // ============================================================================

  /**
   * Cargar tipos de ausencias disponibles
   */
  const loadTypes = useCallback(async () => {
    try {
      const response = await absencesAPI.getTypes();
      console.log('useAbsences - loadTypes response:', response);
      if (response.success) {
        console.log('useAbsences - tipos cargados:', response.types);
        setTypes(response.types);
        return { success: true, types: response.types };
      }
      console.error('useAbsences - loadTypes failed:', response.error);
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar tipos';
      console.error('useAbsences - loadTypes error:', err);
      return { success: false, error: errorMsg };
    }
  }, []);

  // ============================================================================
  // Consultas y Estadísticas
  // ============================================================================

  /**
   * Cargar balance de días para un empleado
   */
  const loadBalance = useCallback(async (employeeId, anio = new Date().getFullYear()) => {
    setLoading(true);
    setError(null);
    try {
      const response = await absencesAPI.getBalance(employeeId, anio);
      if (response.success) {
        setBalance(response.balance);
        return { success: true, balance: response.balance };
      }
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar balance';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cargar estadísticas de ausencias
   */
  const loadStats = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await absencesAPI.getStats(filters);
      if (response.success) {
        setStats(response.stats);
        return { success: true, stats: response.stats };
      }
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar estadísticas';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cargar calendario de ausencias
   */
  const loadCalendar = useCallback(async (startDate, endDate, filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await absencesAPI.getCalendar(startDate, endDate, filters);
      if (response.success) {
        return { success: true, absences: response.absences };
      }
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar calendario';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================================
  // Helpers
  // ============================================================================

  /**
   * Limpiar estado
   */
  const clearState = useCallback(() => {
    setAbsences([]);
    setTypes([]);
    setBalance(null);
    setStats(null);
    setError(null);
    setPagination({ page: 1, limit: 10, totalPages: 1, total: 0 });
  }, []);

  /**
   * Cambiar página
   */
  const changePage = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  }, []);

  return {
    // Estado
    absences,
    types,
    balance,
    stats,
    loading,
    error,
    pagination,

    // Métodos - CRUD
    loadAbsences,
    getById,
    createAbsence,

    // Métodos - Aprobación
    aprobar,
    rechazar,
    cancelar,

    // Métodos - Consultas
    loadTypes,
    loadBalance,
    loadStats,
    loadCalendar,

    // Helpers
    clearState,
    changePage
  };
};

export default useAbsences;
